import { InputRadio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import React, { useEffect } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useLocalStorageJSON } from 'common/hooks';
import { Body3 } from 'common/typography/Typography';
import { MENU_PHOTOSHOOT_OPTIONS_LOCAL_STORAGE_ID, SsmoStepsIdentifiers } from 'modules/onboarding-page/constants';
import { MenuPhotoShootOption } from 'modules/onboarding-page/types';

import FormSection from './FormSection';
import validateYupSchema from '../../../../../common/utils/validateYupSchema';
import { CountryConfig } from '../../../../country/types';
import { RenderPrice } from '../../RenderPrice';
import { MenuPhotoShootOptionPreferences, MenuPhotoShootOptionPreferencesSchema } from '../../validation';

type MenuPhotoShootOptionFormProps = {
  menuPhotoShootPricingConfig: Partial<{ [key in MenuPhotoShootOption]: string | null }>;
  photoShootOptionChoice: MenuPhotoShootOption | undefined;
  handleSubmit: (values: MenuPhotoShootOptionPreferences) => Promise<void | { [key: string]: string | Error }>;
  setNextButtonDisabled: (disabled: boolean) => void;
  countryConfig: CountryConfig;
};

type PhotoShootOptionProps = {
  value: MenuPhotoShootOption;
  title: string;
  price: string;
  description: string;
  countryConfig: CountryConfig;
  isLimitedOffer?: boolean;
};

const FormSectionWrapper = styled.div`
  margin-block-end: 16px;
`;

const RadioInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const RadioInputSectionWrapper = styled.div``;

const RadioInputHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled(Body3)`
  margin-block-end: 12px;
  font-weight: 700;
`;

const Description = styled(Body3)`
  display: flex;
`;

const StyledTag = styled(Tag)`
  margin-block-end: 12px;
`;

const FeeExplanation = styled(Body3)`
  margin-block-start: 16px;
`;

const PhotoShootOption = ({
  title,
  price,
  description,
  value,
  countryConfig,
  isLimitedOffer,
}: PhotoShootOptionProps) => {
  const intl = useIntl();
  const { currency, primary_language } = countryConfig;

  return (
    <FormSectionWrapper>
      <FormSection wrapContentWithLabel>
        <Field<string | undefined> name="menuPhotoShootChoice" type="radio" value={value}>
          {({ input }) => (
            <RadioInputWrapper>
              <>
                <InputRadio
                  {...input}
                  name="menuPhotoShootChoice"
                  style={{
                    width: '24px',
                    height: '24px',
                    marginRight: '12px',
                  }}
                />
                <RadioInputSectionWrapper>
                  <RadioInputHeaderWrapper>
                    <Title>{title}</Title>
                    <Body3>
                      {Number(price) === 0 ? (
                        intl.formatMessage({ id: 'self-serve-sign-up.wizard.menu-photoshoot-options.free' })
                      ) : (
                        <>
                          <RenderPrice primaryLanguage={primary_language} value={price} currency={currency} /> *
                        </>
                      )}
                    </Body3>
                  </RadioInputHeaderWrapper>

                  {isLimitedOffer && (
                    <StyledTag variant="lightBlack">
                      {intl.formatMessage({ id: 'self-serve-sign-up.wizard.limited-offer-tag' })}
                    </StyledTag>
                  )}

                  <Description>{description}</Description>
                </RadioInputSectionWrapper>
              </>
            </RadioInputWrapper>
          )}
        </Field>
      </FormSection>
    </FormSectionWrapper>
  );
};

const MenuPhotoShootOptionForm = ({
  menuPhotoShootPricingConfig,
  photoShootOptionChoice,
  handleSubmit,
  setNextButtonDisabled,
  countryConfig,
}: MenuPhotoShootOptionFormProps) => {
  const intl = useIntl();

  const [storedMenuPhotoShootOptionPreferences, setMenuPhotoShootOptionPreferences] = useLocalStorageJSON<
    Partial<MenuPhotoShootOptionPreferences>
  >(MENU_PHOTOSHOOT_OPTIONS_LOCAL_STORAGE_ID, {
    menuPhotoShootChoice: photoShootOptionChoice,
  });

  useEffect(() => {
    return () => {
      setNextButtonDisabled(false);
    };
  }, [setNextButtonDisabled]);

  return (
    <Form<MenuPhotoShootOptionPreferences>
      initialValues={storedMenuPhotoShootOptionPreferences}
      onSubmit={handleSubmit}
      validate={validateYupSchema(MenuPhotoShootOptionPreferencesSchema)}
    >
      {({ handleSubmit, hasValidationErrors }) => {
        setNextButtonDisabled(hasValidationErrors);

        const hasPaidWoltPhotoshoot = Number(menuPhotoShootPricingConfig.WOLT_PHOTOSHOOT) !== 0;

        return (
          <>
            <FormSpy
              subscription={{ values: true }}
              onChange={(props): void => {
                setMenuPhotoShootOptionPreferences(props.values as MenuPhotoShootOptionPreferences);
              }}
            />
            <form id={SsmoStepsIdentifiers.MenuPhotoShoot} onSubmit={handleSubmit}>
              {'MERCHANT_OWN_PHOTO' in menuPhotoShootPricingConfig && (
                <PhotoShootOption
                  title={intl.formatMessage({
                    id: 'self-serve-sign-up.wizard.menu-photoshoot-options.own-photo.title',
                  })}
                  value="MERCHANT_OWN_PHOTO"
                  description={intl.formatMessage({
                    id: 'self-serve-sign-up.wizard.menu-photoshoot-options.own-photo.description',
                  })}
                  price={menuPhotoShootPricingConfig.MERCHANT_OWN_PHOTO!}
                  countryConfig={countryConfig}
                />
              )}
              {'WOLT_PHOTOSHOOT' in menuPhotoShootPricingConfig && (
                <PhotoShootOption
                  title={intl.formatMessage({
                    id: 'self-serve-sign-up.wizard.menu-photoshoot-options.wolt-photoshoot.title',
                  })}
                  value="WOLT_PHOTOSHOOT"
                  description={intl.formatMessage({
                    id: 'self-serve-sign-up.wizard.menu-photoshoot-options.wolt-photoshoot.description',
                  })}
                  isLimitedOffer={!hasPaidWoltPhotoshoot}
                  price={menuPhotoShootPricingConfig.WOLT_PHOTOSHOOT!}
                  countryConfig={countryConfig}
                />
              )}
            </form>

            {!hasPaidWoltPhotoshoot && (
              <FeeExplanation>
                {intl.formatMessage({ id: 'self-serve-sign-up.wizard.limited-offer-description' })}
              </FeeExplanation>
            )}

            {'WOLT_PHOTOSHOOT' in menuPhotoShootPricingConfig && hasPaidWoltPhotoshoot && (
              <FeeExplanation>
                *{' '}
                {intl.formatMessage({
                  id: 'self-serve-sign-up.wizard.menu-photoshoot-options.fee-explanation',
                })}
              </FeeExplanation>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default MenuPhotoShootOptionForm;
